
import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Upload } from "lucide-react";

interface QuoteRequestFormProps {
  language?: 'ar' | 'en';
}

const QuoteRequestForm = ({ language = 'ar' }: QuoteRequestFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    productType: '',
    quantity: '',
    specifications: '',
    file: null as File | null
  });

  const content = {
    ar: {
      title: "طلب عرض سعر",
      subtitle: "املأ النموذج أدناه وسنتواصل معك لتقديم أفضل عرض سعر لمنتجاتك",
      name: "الاسم *",
      company: "اسم الشركة",
      email: "البريد الإلكتروني *",
      phone: "رقم الهاتف *",
      productType: "نوع المنتج *",
      quantity: "الكمية المطلوبة",
      specifications: "المواصفات والتفاصيل",
      fileUpload: "رفع ملف التصميم (اختياري)",
      submitButton: "إرسال طلب العرض",
      successMessage: "تم إرسال طلبك بنجاح، سنتواصل معك قريباً",
      quantityPlaceholder: "مثال: 1000 قطعة",
      specificationsPlaceholder: "اذكر تفاصيل المنتج، الأحجام، الألوان، متطلبات الطباعة، أو أي مواصفات خاصة",
      selectProduct: "اختر نوع المنتج",
      chooseFile: "اختر ملف",
      dragFile: "أو اسحب الملف هنا",
      fileTypes: "PDF, JPG, PNG, AI, EPS (الحد الأقصى 10MB)",
      fileSelected: "تم اختيار:",
      products: {
        peBags: "أكياس البولي إيثيلين",
        ppProducts: "منتجات البولي بروبلين",
        plasticContainers: "عبوات بلاستيكية",
        shoppingBags: "أكياس التسوق",
        custom: "منتج مخصص"
      }
    },
    en: {
      title: "Request Quote",
      subtitle: "Fill out the form below and we'll contact you to provide the best quote for your products",
      name: "Name *",
      company: "Company Name",
      email: "Email *",
      phone: "Phone Number *",
      productType: "Product Type *",
      quantity: "Required Quantity",
      specifications: "Specifications & Details",
      fileUpload: "Upload Design File (Optional)",
      submitButton: "Send Quote Request",
      successMessage: "Your request has been sent successfully, we will contact you soon",
      quantityPlaceholder: "Example: 1000 pieces",
      specificationsPlaceholder: "Specify product details, sizes, colors, printing requirements, or any special specifications",
      selectProduct: "Select Product Type",
      chooseFile: "Choose File",
      dragFile: "or drag file here",
      fileTypes: "PDF, JPG, PNG, AI, EPS (Max 10MB)",
      fileSelected: "Selected:",
      products: {
        peBags: "Polyethylene Bags",
        ppProducts: "Polypropylene Products",
        plasticContainers: "Plastic Containers",
        shoppingBags: "Shopping Bags",
        custom: "Custom Product"
      }
    }
  };

  const t = content[language];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert(t.successMessage);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({
        ...formData,
        file: e.target.files[0]
      });
    }
  };

  return (
    <section id="quote" className="py-16 md:py-20 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t.title}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8"
        >
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">{t.name}</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">{t.company}</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">{t.email}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">{t.phone}</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">{t.productType}</label>
              <select
                name="productType"
                value={formData.productType}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t.selectProduct}</option>
                <option value="pe-bags">{t.products.peBags}</option>
                <option value="pp-products">{t.products.ppProducts}</option>
                <option value="plastic-containers">{t.products.plasticContainers}</option>
                <option value="shopping-bags">{t.products.shoppingBags}</option>
                <option value="custom">{t.products.custom}</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">{t.quantity}</label>
              <input
                type="text"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                placeholder={t.quantityPlaceholder}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-700 font-semibold mb-2">{t.specifications}</label>
              <textarea
                name="specifications"
                value={formData.specifications}
                onChange={handleInputChange}
                rows={4}
                placeholder={t.specificationsPlaceholder}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-700 font-semibold mb-2">{t.fileUpload}</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="mx-auto w-12 h-12 text-gray-400 mb-4" />
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.ai,.eps"
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-500">{t.chooseFile}</span>
                  <span className="text-gray-500"> {t.dragFile}</span>
                </label>
                <p className="text-sm text-gray-400 mt-2">{t.fileTypes}</p>
                {formData.file && (
                  <p className="text-green-600 mt-2">{t.fileSelected} {formData.file.name}</p>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-blue-600 text-white py-4 px-8 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                {t.submitButton}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default QuoteRequestForm;
